/**
 * Type-coercion helpers used at boundaries where external data (XML/JSON
 * parsers, persisted PouchDB records) becomes typed domain data.
 *
 * Why this lives in its own file: every transformer that consumes XML or
 * legacy persisted records has the same coercion needs. Without a shared
 * helper, each transformer reinvents the same Number/String guards
 * inconsistently. See PR for the F5PLR / `state: 64` bug.
 */

/**
 * Convert a string|number|undefined into a finite number, or undefined.
 *
 * Returns undefined for: undefined, null, "", non-finite results (NaN/Infinity).
 * Used by the QRZ/HamQTH transformers to coerce genuinely-numeric XML fields
 * (dxcc, lat/lon, zones) back to numbers after the parser has been
 * configured to leave every tag value as a string.
 */
export const toNumber = (value: string | number | undefined | null): number | undefined => {
  if (value === undefined || value === null) return undefined;
  // `Number("   ")` is 0, not NaN, so empty/whitespace strings need an explicit
  // guard — otherwise blank XML tags like `<lat></lat>` silently become 0.
  if (typeof value === 'string' && value.trim() === '') return undefined;
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : undefined;
};

/**
 * Convert any non-string value to its string form, leaving strings/null/undefined alone.
 *
 * Used when reading legacy QSO records out of PouchDB whose text-typed fields
 * (state, cnty, etc.) may have been persisted as numbers due to a long-fixed
 * XML-parser auto-coercion bug. See `coerceQsoTextFields`.
 */
export const toStringOrPassthrough = (value: unknown): unknown => {
  if (value === undefined || value === null || typeof value === 'string') return value;
  return String(value);
};
